import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, CalendarDays, Copy, Loader2, Play, Square } from 'lucide-react';
import { Button } from '../ui/Button';
import { ApiError } from '../../lib/api';
import { sesionApi, sesionKeys, type SesionResumen } from '../../lib/sesion';

/**
 * QUÉ DÍA se conduce — y cómo armar uno nuevo copiando otro.
 *
 * Las dos cosas faltaban por completo, y de ahí salió el desorden del sprint
 * CCJ (agosto 2026):
 *
 *   · No había forma de decir «hoy corremos ESTE día». Todas las sesiones
 *     nacen 'preparada' en el seed y las superficies adivinaban («la primera
 *     no cerrada» = el día 1 del sprint). El `en_curso` de la jornada real
 *     hubo que ponerlo a mano por SQL.
 *   · No había forma de repetir un día. Cuando la ruta planeada no sobrevivió
 *     al día 1 y hubo que correr sus herramientas varias veces más, se clonó
 *     por consola: de ahí los procesos duplicados y las sesiones repetidas que
 *     después mandaron los QR a la sala equivocada.
 *
 * El día elegido acá manda sobre toda heurística (`elegirSesionActiva`): quien
 * conduce sabe qué está mirando.
 */
export function SelectorJornada({
  procesoId,
  sesionId,
  onElegir,
  puedeConducir,
}: {
  procesoId: string;
  /** Jornada que la Cabina está mostrando ahora. */
  sesionId: string | null;
  /** Fija ?sesion= en la URL. */
  onElegir: (sesionId: string) => void;
  puedeConducir: boolean;
}) {
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [clonando, setClonando] = useState(false);
  const [origen, setOrigen] = useState('');

  // Misma queryKey que useSesionActiva: comparte caché, no agrega peticiones.
  const proceso = useQuery({
    queryKey: sesionKeys.proceso(procesoId),
    queryFn: () => sesionApi.proceso(procesoId),
  });

  const jornadas = useMemo(() => {
    const fases = [...(proceso.data?.fases ?? [])].sort((a, b) => a.orden - b.orden);
    return fases.flatMap((f) =>
      f.sesiones.map((s) => ({
        ...s,
        // La sesión de validación de documentos es una jornada REAL (la del
        // día en que se validan los documentos), pero asíncrona: se rotula
        // con su identidad propia para que nunca se confunda con la jornada
        // presencial de la misma fase.
        fase: s.validaciones ? 'Validación de documentos · asíncrona' : f.nombre,
      })),
    );
  }, [proceso.data]);

  const actual = jornadas.find((j) => j.id === sesionId) ?? null;
  const enCurso = actual?.estado === 'en_curso';

  /** Refresca todo lo que depende de qué día se corre. */
  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: sesionKeys.proceso(procesoId) });
    if (sesionId) void qc.invalidateQueries({ queryKey: sesionKeys.sesion(sesionId) });
  };

  const conducir = useMutation({
    mutationFn: (accion: 'iniciar' | 'cerrar') =>
      accion === 'iniciar'
        ? sesionApi.iniciarSesion(sesionId!)
        : sesionApi.cerrarSesion(sesionId!),
    onSuccess: () => {
      setError(null);
      refrescar();
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.detail : 'No pudimos cambiar la jornada.'),
  });

  const clonar = useMutation({
    mutationFn: (modo: 'agregar' | 'reemplazar') =>
      sesionApi.clonarActividades(sesionId!, origen, modo),
    onSuccess: (res) => {
      setError(null);
      setClonando(false);
      setOrigen('');
      refrescar();
      void qc.invalidateQueries({ queryKey: sesionKeys.sesion(res.sesion_id) });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.detail : 'No pudimos copiar la jornada.'),
  });

  if (!puedeConducir || jornadas.length === 0) return null;

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex min-h-touch items-center gap-2 font-sans text-sm text-content-muted">
          <CalendarDays className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="shrink-0">Día</span>
          <select
            value={sesionId ?? ''}
            onChange={(e) => {
              setError(null);
              onElegir(e.target.value);
            }}
            className="max-w-[15rem] truncate rounded-md border border-border-strong bg-surface-raised px-2 py-1.5 font-sans text-sm font-semibold text-content focus-visible:outline-none focus-visible:shadow-focus"
            aria-label="Jornada que estás conduciendo"
          >
            {jornadas.map((j) => (
              <option key={j.id} value={j.id}>
                {etiquetaJornada(j)}
              </option>
            ))}
          </select>
        </label>

        {sesionId && !enCurso && (
          <Button
            variant="primary"
            size="sm"
            loading={conducir.isPending && conducir.variables === 'iniciar'}
            onClick={() => conducir.mutate('iniciar')}
            leadingIcon={<Play className="h-4 w-4" aria-hidden="true" />}
            title="Las tres superficies pasan a este día y el QR lleva acá"
          >
            Activar este día
          </Button>
        )}

        {sesionId && enCurso && (
          <>
            <span className="flex items-center gap-1.5 rounded-md bg-surface-sunken px-2 py-1 font-sans text-xs font-semibold text-success">
              <span className="h-2 w-2 animate-pulse rounded-full bg-success" aria-hidden="true" />
              En curso
            </span>
            <Button
              variant="secondary"
              size="sm"
              loading={conducir.isPending && conducir.variables === 'cerrar'}
              onClick={() => conducir.mutate('cerrar')}
              leadingIcon={<Square className="h-4 w-4" aria-hidden="true" />}
              title="Cierra la jornada: el código de sala deja de abrir"
            >
              Cerrar jornada
            </Button>
          </>
        )}

        {sesionId && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setClonando((v) => !v);
              setError(null);
            }}
            leadingIcon={<Copy className="h-4 w-4" aria-hidden="true" />}
            title="Copia las actividades de otro día a este"
          >
            Copiar de otro día
          </Button>
        )}
      </div>

      {clonando && sesionId && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2">
          <label className="flex min-h-touch items-center gap-2 font-sans text-xs text-content-muted">
            Copiar las actividades de
            <select
              value={origen}
              onChange={(e) => setOrigen(e.target.value)}
              className="max-w-[14rem] truncate rounded border border-border-strong bg-surface-raised px-2 py-1 font-sans text-sm text-content focus-visible:outline-none focus-visible:shadow-focus"
              aria-label="Jornada de la que se copian las actividades"
            >
              <option value="">Elegí un día…</option>
              {jornadas
                .filter((j) => j.id !== sesionId)
                .map((j) => (
                  <option key={j.id} value={j.id}>
                    {etiquetaJornada(j)}
                  </option>
                ))}
            </select>
          </label>
          <Button
            variant="primary"
            size="sm"
            disabled={!origen}
            loading={clonar.isPending && clonar.variables === 'agregar'}
            onClick={() => clonar.mutate('agregar')}
            title="Encola las actividades al final de este día"
          >
            Agregar al final
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={!origen}
            loading={clonar.isPending && clonar.variables === 'reemplazar'}
            onClick={() => clonar.mutate('reemplazar')}
            title="Vacía lo que no haya corrido y copia; nada con aportes se borra"
          >
            Reemplazar
          </Button>
          <p className="w-full font-sans text-xs text-content-muted">
            Se copia la receta del día (herramientas, orden, duración,
            conexiones). No viaja ni un aporte: las copias nacen preparadas.
          </p>
        </div>
      )}

      {clonar.isSuccess && !clonando && (
        <p className="flex items-center gap-1.5 font-sans text-xs text-success" role="status">
          <Loader2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {clonar.data.creadas.length} actividades copiadas a este día.
        </p>
      )}

      {error && (
        <p className="flex items-center gap-1.5 font-sans text-xs text-error" role="alert">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
}

const MESES = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

/** «6 ago · Entender · en curso» — el facilitador reconoce el día de un vistazo. */
function etiquetaJornada(j: SesionResumen & { fase: string }): string {
  const partes: string[] = [];
  if (j.fecha) {
    const [, m, d] = j.fecha.split('-').map(Number);
    partes.push(`${d} ${MESES[m - 1] ?? ''}`.trim());
  } else {
    partes.push('sin fecha');
  }
  if (j.fase) partes.push(j.fase);
  if (j.estado === 'en_curso') partes.push('en curso');
  else if (j.estado === 'cerrada') partes.push('cerrada');
  return partes.join(' · ');
}
